import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { choroplethFillColor } from '../../utils/adminChoropleth';
import {
  adminMapContainerStyle,
  createAdminChoroplethMap,
} from '../../utils/adminMapConfig';

interface AdminChoroplethMapProps {
  id: string;
  geoJson: GeoJSON.FeatureCollection | null;
  center: [number, number];
  zoom: number;
  height?: string;
  /** Label for the count metric, e.g. "Country" or "US state" */
  regionKind: string;
}

function formatCount(n: number): string {
  return n.toLocaleString();
}

function buildPopupHtml(
  regionKind: string,
  name: string,
  code: string,
  count: number,
): string {
  return `
    <div style="font-size: 13px; line-height: 1.45;">
      <strong>${name}</strong><br/>
      <span style="color: #6c757d;">${regionKind} · ${code}</span><br/>
      <span style="margin-top: 6px; display: inline-block;">
        Collections: <strong>${formatCount(count)}</strong>
      </span>
    </div>
  `;
}

export default function AdminChoroplethMap({
  id,
  geoJson,
  center,
  zoom,
  height = '360px',
  regionKind,
}: AdminChoroplethMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);

  const sourceId = `${id}-source`;
  const layerId = `${id}-layer`;
  const outlineLayerId = `${id}-outline`;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = createAdminChoroplethMap(containerRef.current, center, zoom);
    mapRef.current = map;

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, [center, zoom, id]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !geoJson) return;

    const setupLayers = () => {
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, { type: 'geojson', data: geoJson });
        map.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': choroplethFillColor as mapboxgl.Expression,
            'fill-opacity': 0.9,
          },
        });
        map.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': '#6c757d',
            'line-width': 0.6,
          },
        });

        map.on('click', layerId, (e) => {
          const feature = e.features?.[0];
          if (!feature?.properties || !e.lngLat) return;
          const code = String(feature.properties.regionCode || '');
          const count = Number(feature.properties.count) || 0;
          const name = String(feature.properties.regionName || code);
          if (!code) return;

          popupRef.current?.remove();
          popupRef.current = new mapboxgl.Popup({
            closeButton: true,
            closeOnClick: true,
            maxWidth: '260px',
          })
            .setLngLat(e.lngLat)
            .setHTML(buildPopupHtml(regionKind, name, code, count))
            .addTo(map);
        });

        map.on('mouseenter', layerId, () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', layerId, () => {
          map.getCanvas().style.cursor = '';
        });
      } else {
        (map.getSource(sourceId) as mapboxgl.GeoJSONSource).setData(geoJson);
      }
    };

    if (map.isStyleLoaded()) {
      setupLayers();
    } else {
      map.once('load', setupLayers);
    }
  }, [geoJson, sourceId, layerId, outlineLayerId, regionKind]);

  return (
    <div ref={containerRef} style={{ ...adminMapContainerStyle, height }} />
  );
}
